import React, { useMemo, useState } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  Loader2,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw,
} from 'lucide-react';
import Header from '../../components/Header/Header';
import { creditService } from '../../api/services';
import { useCredits } from '../../contexts/CreditsContext';
import { transactionLabel, creditLevel } from '../../utils/creditLabels';
import { useQuery } from '@tanstack/react-query';

const PAGE_SIZE = 20;

const dateFormatter = new Intl.DateTimeFormat('fr-FR', {
  dateStyle: 'medium',
  timeStyle: 'short',
});

const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : dateFormatter.format(d);
};

const isAdminUser = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return (
      user?.isPlatformAdmin === true ||
      user?.isAdmin === true ||
      ['admin', 'owner', 'platform_admin'].includes(user?.role)
    );
  } catch {
    return false;
  }
};

export default function CreditsHistory() {
  const { balance } = useCredits();
  const showInternal = useMemo(() => isAdminUser(), []);

  const [page, setPage] = useState(1);

  const { data: txData, isLoading: loading, isError, error, isFetching } = useQuery({
    queryKey: ['credits-transactions', page],
    queryFn: () => creditService.getTransactions({ page, limit: PAGE_SIZE }),
    placeholderData: (prev) => prev,
  });

  const transactions = Array.isArray(txData?.data) ? txData.data : [];
  const pagination = txData?.pagination || { page: 1, limit: PAGE_SIZE, total: 0, pages: 1 };

  const totalPages = Math.max(1, pagination.pages || 1);
  const colSpan = showInternal ? 5 : 4;

  return (
    <div>
      <Header />

      {/* En-tête de page */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <p className="text-xs2 mb-1" style={{ color: 'var(--c-text-muted)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            Compte
          </p>
          <div className="flex items-center gap-2">
            <h1 className="text-ink font-semibold" style={{ fontSize: '18px' }}>
              Historique des crédits
            </h1>
            {isFetching && !loading && (
              <RefreshCw size={13} className="animate-spin" style={{ color: 'var(--c-text-muted)' }} />
            )}
          </div>
        </div>
        <div className="text-right">
          <p className="text-label mb-0.5">Solde actuel</p>
          <p
            className={`font-metric text-2xl font-bold ${
              balance === null
                ? 'text-label'
                : creditLevel(balance) === 'danger'
                ? 'text-red-400'
                : creditLevel(balance) === 'warning'
                ? 'text-amber-300'
                : 'text-accent-green'
            }`}
          >
            {balance === null ? '—' : balance}
            <span className="text-xs2 text-label ml-1">crédits</span>
          </p>
        </div>
      </div>

      <div className="card overflow-hidden">
        {isError ? (
          <div className="p-6">
            <div className="flex items-start gap-3 p-4 rounded-lg" style={{ background: 'rgba(224,84,84,0.08)', border: '0.5px solid rgba(224,84,84,0.3)' }}>
              <AlertCircle style={{ color: 'var(--c-error)', flexShrink: 0, marginTop: 2 }} size={20} />
              <div>
                <p className="text-sm font-medium" style={{ color: 'var(--c-error)' }}>{error?.message || "Impossible de charger l'historique des crédits."}</p>
                <button
                  onClick={() => setPage(1)}
                  className="btn-primary mt-2"
                  style={{ height: '30px', fontSize: '12px' }}
                >
                  Réessayer
                </button>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left" style={{ borderBottom: '0.5px solid var(--c-border)' }}>
                    <th className="px-4 py-3 font-medium text-label">Date</th>
                    <th className="px-4 py-3 font-medium text-label">Type / Action</th>
                    <th className="px-4 py-3 font-medium text-label text-right">Variation</th>
                    <th className="px-4 py-3 font-medium text-label text-right">Solde après</th>
                    {showInternal && (
                      <th className="px-4 py-3 font-medium text-label text-right">Détails (interne)</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={colSpan} className="px-4 py-12 text-center text-label">
                        <Loader2 className="animate-spin mx-auto mb-2 text-accent-green" size={24} />
                        Chargement de l'historique…
                      </td>
                    </tr>
                  ) : transactions.length === 0 ? (
                    <tr>
                      <td colSpan={colSpan} className="px-4 py-12 text-center text-label">
                        Aucune transaction de crédits pour le moment.
                      </td>
                    </tr>
                  ) : (
                    transactions.map((tx) => {
                      const isCredit = Number(tx.credits) > 0;
                      return (
                        <tr
                          key={tx.id}
                          className="last:border-0 transition-colors hover:bg-[var(--c-hover)]"
                          style={{ borderBottom: '0.5px solid var(--c-border)' }}
                        >
                          <td className="px-4 py-3 text-label whitespace-nowrap">
                            {formatDate(tx.createdAt)}
                          </td>
                          <td className="px-4 py-3 text-ink">{transactionLabel(tx)}</td>
                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            <span
                              className={`inline-flex items-center gap-1 font-semibold tabular-nums ${
                                isCredit ? 'text-accent-green' : 'text-red-400'
                              }`}
                            >
                              {isCredit ? (
                                <ArrowUpRight size={14} />
                              ) : (
                                <ArrowDownRight size={14} />
                              )}
                              {isCredit ? `+${tx.credits}` : tx.credits}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right text-ink font-medium tabular-nums">
                            {tx.balanceAfter}
                          </td>
                          {showInternal && (
                            <td className="px-4 py-3 text-right text-xs text-label whitespace-nowrap">
                              {tx.model ? (
                                <span title={`${tx.tokens ?? '—'} tokens · ${tx.costUsd ?? '—'} $`}>
                                  {tx.model}
                                </span>
                              ) : (
                                '—'
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination serveur */}
            <div className="flex items-center justify-between px-4 py-3" style={{ borderTop: '0.5px solid var(--c-border)' }}>
              <p className="text-xs text-label">
                {pagination.total > 0
                  ? `${pagination.total} transaction${pagination.total > 1 ? 's' : ''} · page ${page} / ${totalPages}`
                  : '—'}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={loading || page <= 1}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-ink text-sm disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}
                >
                  <ChevronLeft size={16} /> Précédent
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={loading || page >= totalPages}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-ink text-sm disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}
                >
                  Suivant <ChevronRight size={16} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
